<template>
  <modal
    v-if="show"
    width="620px"
    :title="t('Generate Links')"
    @close="close"
    bodyOverflow="auto"
    :read-article-url="`https://docs.bemob.com/${locale}/custom-conversions`"
  >
    <ui-select
      :modelValue="selectedWorkspaceId"
      @change="onChangeWorkspace"
      :items="workspacesList"
      :label="t('Workspace')"
      item-text="name"
      item-value="id"
      max-width-calc
      :hide-details="2"
      class="mb-4"
    />

    <ui-select
      v-model="selectedCampaign"
      :items="allCampaigns"
      :loading="campaignsLoading"
      :label="t('Campaign')"
      item-text="nameFull"
      :fetchOptions="fetchCampaignOptions"
      :error-messages="
        !campaignsLoading &&
        allCampaigns.length === 0 &&
        t('Campaign field can not be empty. Select another workspace')
      "
      :return-object="true"
    />

    <ui-radio-group
      v-model="role"
      class="role"
      :hideMarginTop="true"
      :label="t('Role')"
      :labelHelp="
        t(
          'Select the required option to generate the list of tracking links for custom conversion event.',
        )
      "
    >
      <div>
        <div class="affiliateNetwork--radio-button affiliate-wrapper mb-4">
          <ui-radio
            :hideBottomMargin="true"
            value="fromAffiliateNetwork"
            :label="t('Select Existing Affiliate Network')"
          />
          <ui-select
            v-model="selectedAffiliateNetworks"
            :items="getAllAffiliateNetworks"
            :loading="!store.cache.affiliateNetworks.isUploaded"
            class="ml-16 affiliate-network-select"
            :class="{
              'opacity-0': role === 'enterManually',
            }"
            item-text="name"
            :disabled="role === 'enterManually'"
            :return-object="true"
            :hide-details="1"
          />
        </div>
        <ui-radio value="enterManually" :label="t('Enter Tokens Manually')" />
      </div>
    </ui-radio-group>

    <div class="params">
      <ui-input
        class="param"
        :disabled="role === 'fromAffiliateNetwork'"
        v-model="form.clickId"
        :label="t('Click ID')"
        :placeholder="role === 'enterManually' ? t('Type Token') : undefined"
      />

      <ui-input
        class="param"
        :disabled="role === 'fromAffiliateNetwork'"
        v-model="form.payout"
        :label="t('Payout')"
        :placeholder="role === 'enterManually' ? t('Type Token') : undefined"
      />

      <ui-input
        class="param"
        :disabled="role === 'fromAffiliateNetwork'"
        v-model="form.transactionId"
        :label="t('Transaction ID')"
        :placeholder="role === 'enterManually' ? t('Type Token') : undefined"
      />

      <ui-select
        class="param"
        v-model="form.cet"
        :items="item.values"
        :label="t('Conversion Value')"
      />
    </div>

    <ui-copy-input :value="s2sPostback" :label="t('S2S Postback')" />

    <ui-copy-input :value="conversionPixelURL" :label="t('Conversion Pixel URL')" />

    <ui-copy-input :value="conversionPixel" :label="t('Conversion Pixel')" />

    <ui-copy-input :value="conversionScript" :label="t('Conversion Script')" />

    <ui-copy-input
      :value="conversionPixelURLTxt"
      :label="t('Postback URL (for TXT response)')"
      :labelHelp="
        t(
          'This postback URL is required in cases when your affiliate network is requiring the response on successful conversion action in TXT format.',
        )
      "
    />

    <template #footer>
      <div class="d-flex justify-end">
        <ui-button
          :text="t('Close')"
          icon="fas fa-times"
          :disabled="loading"
          @click="close"
        />
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { StatusEnum } from '@/services/models/Enums';
import { getCampaignsApiRequest } from '@/services/api/campaign-api';
import {
  populateCampaignsStoreCacheApiRequest,
  updateCampaignsStoreCacheApiRequest,
} from '@/services/api/api-services/campaigns';
import { populateTrackingDomainsStoreCacheApiRequest } from '@/services/api/api-services/domains';
import { populateAffiliateNetworksStoreCacheApiRequest } from '@/services/api/api-services/affiliate-networks';
import type { UserWorkspace } from '@/services/models';
import { useI18n } from '@/composables';
import { useStore } from '@/store';

type AffiliateNetworkTemplate = {
  clickIdPlaceholder?: string;
  payoutPlaceholder?: string;
  transactionIdPlaceholder?: string;
};

type AffiliateNetworkItem = {
  id: string;
  name: string;
  workspaceId: string;
  affiliateNetworkTemplate?: AffiliateNetworkTemplate;
};

type CustomConversionItem = {
  values: string[];
};

const props = defineProps<{
  data?: CustomConversionItem;
}>();

const emit = defineEmits<{ close: [] }>();
const { t, locale } = useI18n();
const store = useStore();
const workspacesList = computed((): UserWorkspace[] => store.allPrivateWorkspaces);
const defaultWorkspace = computed((): UserWorkspace | undefined => {
  const selectedWorkspaceIds = store.user?.selectedWorkspaces ?? [];
  const list = workspacesList.value;
  return (
    (selectedWorkspaceIds.length &&
      list.find((workspace) => selectedWorkspaceIds.includes(workspace.id))) ||
    list.find((workspace) => workspace.name === 'Master') ||
    list[0]
  );
});

const show = ref(false);
const loading = ref(false);
const campaignsLoading = ref(true);

const allCampaigns = ref<Bemob.Campaign[]>([]);
const selectedCampaign = ref<Bemob.Campaign | null>(null);
const selectedWorkspaceId = ref('');
const selectedAffiliateNetworks = ref<AffiliateNetworkItem | null>(null);
const item = ref<CustomConversionItem>({ values: [] });

const form = reactive({
  clickId: '',
  payout: '',
  transactionId: '',
  cet: '',
});

const role = ref<'enterManually' | 'fromAffiliateNetwork'>('enterManually');

const s2sPostback = computed(() => `${domain.value}/postback${urlParams('REPLACE')}`);

const conversionPixelURL = computed(
  () => `${domain.value}/conversion.gif${urlParams('OPTIONAL')}`,
);

const conversionPixel = computed(
  () =>
    `<img src="${domain.value}/conversion.gif${urlParams('OPTIONAL')}" width="1" height="1" border="0" alt="" />`,
);

const conversionScript = computed(
  () =>
    `<sc${''}ript async type="text/javascript" src="${domain.value}/conversion.js${urlParams('OPTIONAL')}"></sc${''}ript>`,
);

const conversionPixelURLTxt = computed(
  () => `${domain.value}/conversion.txt${urlParams('OPTIONAL')}`,
);

const urlParams = (clickIdDefault: string) => {
  const cet = `cet=${form.cet}`;
  const cid = `cid=${form.clickId || clickIdDefault}`;
  const payout = `payout=${form.payout || 'OPTIONAL'}`;
  const txid = `txid=${form.transactionId || 'OPTIONAL'}`;
  return `?${cet}&${cid}&${payout}&${txid}`;
};

const domain = computed(() => {
  if (!selectedCampaign.value) return 'https://yourdomain.com/';
  const trackingDomainsObject = store.trackingDomainsObject;
  const trackingDomain =
    trackingDomainsObject[selectedCampaign.value.trackingDomainId] ||
    trackingDomainsObject[store.user?.trackingDomainId];

  return trackingDomain
    ? `${trackingDomain.httpsAvailable ? 'https' : 'http'}://${trackingDomain.domain}`
    : 'https://yourdomain.com/';
});

onMounted(async () => {
  store.setLoading(true);
  try {
    selectedWorkspaceId.value = defaultWorkspace.value?.id ?? '';

    if (!store.cache.trackingDomains.isUploaded) {
      await populateTrackingDomainsStoreCacheApiRequest();
    }

    if (!store.cache.affiliateNetworks.isUploaded) {
      await populateAffiliateNetworksStoreCacheApiRequest();
    }

    if (!store.cache.campaigns.isUploaded) {
      await populateCampaignsStoreCacheApiRequest();
    }

    await setCampaignOptions();
    item.value = props.data ?? { values: [] };
    form.cet = item.value.values?.[0] ?? '';
    show.value = true;
  } finally {
    store.setLoading(false);
  }
});

const close = () => {
  emit('close');
};

const onChangeWorkspace = (value: string) => {
  if (typeof value !== 'string' || !value) return;
  selectedWorkspaceId.value = value;
  setCampaignOptions();
};

const getAllAffiliateNetworks = computed(() =>
  store.cache.affiliateNetworks.data.filter(item => {
    return (
      store.allWorkspacesObject[item.workspaceId]?.type === 'public' ||
      item.workspaceId === selectedWorkspaceId.value
    );
  }),
);

const setCampaignOptions = async () => {
  campaignsLoading.value = true;
  try {
    const { isAllInCache, data, isUploaded, uploadedForWorkspace } =
      store.cache.campaigns;

    if (isUploaded && isAllInCache) {
      const campaigns = data.filter(
        item => item.workspaceId === selectedWorkspaceId.value,
      );
      allCampaigns.value = campaigns;
      selectedCampaign.value = campaigns[0] ?? null;
      return;
    }

    if (
      isUploaded &&
      !isAllInCache &&
      uploadedForWorkspace === selectedWorkspaceId.value
    ) {
      allCampaigns.value = data;
      selectedCampaign.value = data[0] ?? null;
      return;
    }

    if (selectedWorkspaceId.value === defaultWorkspace.value?.id) {
      const campaigns = await updateCampaignsStoreCacheApiRequest(
        selectedWorkspaceId.value,
      );
      allCampaigns.value = campaigns;
      selectedCampaign.value = campaigns[0] ?? null;
      return;
    }

    const campaigns = await getCampaignsApiRequest({
      status: StatusEnum.active,
      limit: 200,
      orderBy: 'updated',
      orderDirection: 'desc',
      workspaceIds: [selectedWorkspaceId.value],
    });
    allCampaigns.value = campaigns;
    selectedCampaign.value = campaigns[0] ?? null;
  } finally {
    campaignsLoading.value = false;
  }
};

watch(role, () => {
  if (role.value === 'fromAffiliateNetwork') {
    updateFromAffiliateNetwork();
    return;
  }
  form.clickId = '';
  form.payout = '';
  form.transactionId = '';
});

const updateFromAffiliateNetwork = () => {
  const template = selectedAffiliateNetworks.value?.affiliateNetworkTemplate;
  form.clickId = template?.clickIdPlaceholder ?? '';
  form.payout = template?.payoutPlaceholder ?? '';
  form.transactionId = template?.transactionIdPlaceholder ?? '';
};

watch(selectedAffiliateNetworks, () => {
  if (role.value === 'fromAffiliateNetwork') {
    updateFromAffiliateNetwork();
  }
});

const fetchCampaignOptions = computed(() => {
  if (store.cache.campaigns.isAllInCache) return undefined;
  const workspaceId = selectedWorkspaceId.value;
  return ({ search }: { search: string }) =>
    getCampaignsApiRequest({
      status: StatusEnum.active,
      search,
      limit: 200,
      orderBy: 'updated',
      orderDirection: 'desc',
      workspaceIds: [workspaceId],
    });
});
</script>

<style lang="scss">
.create-custom-dialog__details {
  background: var(--color-gray5);
}
</style>

<style lang="scss" scoped>
.affiliate-wrapper {
  width: fit-content;
}

.affiliate-network-select {
  min-width: 160px;

  :deep(.ui-select__wrapper),
  :deep(.v-input),
  :deep(.v-input__control),
  :deep(.v-field) {
    width: 100%;
  }
}

.params {
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  .param:last-child {
    padding-right: 0;
  }

  @media (max-width: 500px) {
    flex-wrap: wrap;
    gap: 16px;

    .param {
      flex: 1 1 calc((100% / 2) - 32px);
      padding: 0;
    }
  }
}

.param {
  width: 25%;
  flex-shrink: 1;
  padding-right: 16px;
}

.affiliateNetwork--radio-button {
  display: flex;
  align-items: center;
}
</style>
